import React from "react";
import { Page, Text, View, StyleSheet, Image, Font } from "@react-pdf/renderer";
import moment from "moment";
import HomeIcon from "../../../../assets/pdf/directionIcon.svg";
import {
  IDownloadPdfTrip,
  IDownloadPdfTrips,
} from "../../../../types/DownloadPdfTypes";

const styles = StyleSheet.create({
  card: {
    border: "1px solid #D9D9D9",
    padding: 8,
    borderRadius: 10,
  },
  row: {
    flexDirection: "row",
    alignItems: "center",
    gap: 3,
  },
  boldText14: {
    fontSize: 14,
    fontWeight: 700,
    fontFamily: "Helvetica",
    color: "#000",
    lineHeight: 1.4,
    marginBottom: 2,
  },
  normalText14: {
    fontSize: 14,
    fontFamily: "Helvetica",
    fontWeight: 400,
    marginTop: 10,
  },
  normalText20: {
    fontSize: 20,
    fontFamily: "Helvetica",
  },
  boldText16: {
    fontSize: 16,
    fontWeight: "bold",
    fontFamily: "Helvetica",
    fontStyle: "normal",
  },
  normalGrayText14: {
    fontSize: 14,
    fontFamily: "Helvetica",
    color: "#2E2F32",
    fontWeight: 400,
    fontStyle: "normal",
    marginBottom: 4,
  },
  italicText14: {
    fontSize: 14,
    fontStyle: "italic",
    fontFamily: "Helvetica",
  },
  solidDivider: {
    borderTop: "1px solid #D9D9D9",
    marginVertical: 8,
  },
  timelineItem: {
    marginBottom: 8,
  },
  timelineItemContent: {
    flexDirection: "row",
    justifyContent: "space-between",
  },
  timelineDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: "black",
  },

  rowContanier: {
    flexDirection: "row",
  },
  col: {
    flex: 1,
    padding: 15,
  },
  colLeft: {
    flex: 1,
  },
  colRight: {
    flex: 1,
    maxHeight: "420px",
  },
  circle: {
    width: 8,
    height: 8,
    borderRadius: 4,
    borderWidth: 1,
    borderColor: "#6C1FD0",
    backgroundColor: "#6C1FD0",
    marginBottom: 8,
  },
  line: {
    width: 1,
    height: 108,
    backgroundColor: "#6C1FD0",
    marginBottom: 8,
  },
  verticalLayout: {
    alignItems: "center",
  },
  tripColLeft: {
    flex: 1,
    backgroundColor: "#FFFFFF",
    padding: 10,
    marginTop: 3,
  },
  tripColRight: {
    flex: 23,
    backgroundColor: "#FFFFFF",
    padding: 10,
  },
  dropOffContainer: {
    flexDirection: "row",
    alignItems: "flex-start",
    height: "auto",
    marginTop: 10,
  },
  pickUpContainer: {
    flexDirection: "row",
    alignItems: "flex-start",
    height: "auto",
    marginBottom: 15,
  },
  leftColumn: {
    width: "45%",
    paddingRight: 10,
  },
  rightColumn: {
    width: "60%",
    paddingLeft: 10,
    paddingTop: 13,
  },
  verticalLine: {
    width: 1,
    height: "100%",
    backgroundColor: "#D9D9D9",
  },
  tripText: {
    fontSize: "20px",
    fontWeight: 400,
  },
  milesText: {
    fontSize: "12px",
    fontStyle: "normal",
    fontWeight: 400,
  },
  tripCardStyle: {
    alignItems: "center",
  },
  lineRow: {
    flexDirection: "row",
    alignItems: "flex-start",
    gap: 3,
  },
  notesStyle: {
    fontSize: 12,
    fontStyle: "normal",
    fontWeight: 400,
  },
  pickUpVerticalLine: {
    width: 1,
    height: 107,
    backgroundColor: "#D9D9D9",
  },
});

type Props = {
  record: IDownloadPdfTrip;
  legNum: number;
  miles: number;
  wandaCustomerCareNumber: string;
};

const DownloadPdfTripLegCard: React.FC<Props> = ({
  record,
  legNum,
  miles,
  wandaCustomerCareNumber,
}) => {
  return (
    <View style={styles.card}>
      <View style={[styles.rowContanier, styles.tripCardStyle]}>
        <View style={[styles.col, styles.colLeft]}>
          <View style={[styles.row, styles.boldText14]}>
            <Text style={styles.tripText}>Trip {legNum}</Text>
            <Text style={styles.milesText}>{miles} miles</Text>
          </View>
          ``
          {!record.isWillCall ? (
            <>
              <View style={[styles.row, styles.boldText16]}>
                <Text style={styles.boldText16}>
                  {moment(record.requestPickTime).format("hh:mm A")}
                </Text>
                {!record.isWillCall && <Text>Pick up time</Text>}
              </View>
              <View style={styles.normalText14}>
                <Text>
                  Driver will arrive at the pick up address by{" "}
                  {moment(record.requestPickTime).format("hh:mm A")}
                </Text>
              </View>
            </>
          ) : (
            <>
              <View style={[styles.row, styles.boldText16]}>
                <Text style={styles.boldText16}>
                  I'll call when {record.firstName} is ready to be picked up
                </Text>
                {!record.isWillCall && <Text>Pick up time</Text>}
              </View>
              <View style={styles.normalText14}>
                <Text>Call us at {wandaCustomerCareNumber} for pick up.</Text>
              </View>
            </>
          )}
          <View style={styles.solidDivider} />
          <View style={styles.lineRow}>
            <View style={styles.tripColLeft}>
              <View style={styles.verticalLayout}>
                <View style={styles.circle}></View>
                <View style={styles.line}></View>
                <View style={styles.circle}></View>
              </View>
            </View>
            <View style={styles.tripColRight}>
              <View style={styles.pickUpContainer}>
                <View style={styles.leftColumn}>
                  <Text style={styles.boldText14}>Pick up</Text>
                  <Text style={styles.normalGrayText14}>
                    {record?.pickUpAddress1} {record?.pickUpCity},{" "}
                    {record?.pickUpState}
                  </Text>
                </View>

                {record.pickupNotes.length > 185 ? (
                  <View style={styles.verticalLine}></View>
                ) : (
                  <View style={styles.pickUpVerticalLine}></View>
                )}

                <View style={styles.rightColumn}>
                  <Text style={styles.notesStyle}>{record.pickupNotes}</Text>
                </View>
              </View>
              <View style={styles.dropOffContainer}>
                <View style={styles.leftColumn}>
                  <Text style={styles.boldText14}> Drop off</Text>
                  <Text style={styles.normalGrayText14}>
                    {record.dropOffAddress1} {record?.dropOffCity},{" "}
                    {record?.dropOffState}
                  </Text>
                </View>

                <View style={styles.verticalLine}></View>

                <View style={styles.rightColumn}>
                  <Text style={styles.notesStyle}>{record.dropNotes}</Text>
                </View>
              </View>
            </View>
          </View>
        </View>
        <View style={[styles.col, styles.colRight]}>
          {record?.pdfMapImageData !== "" && (
            <Image src={record?.pdfMapImageData} />
          )}
        </View>
      </View>
    </View>
  );
};

export default DownloadPdfTripLegCard;
